/**
 * TV Guide — EPG grid showing what's on each live channel right now and in
 * the next/past few hours. Click a programme block to play it.
 *   • Now / future → live stream
 *   • Past + xtream catch-up available → catchup stream
 * Pure client-side: relies on channels + epgByChannel already loaded by
 * PlaylistDataContext.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { usePlaylistData } from "../lib/PlaylistDataContext";
import { buildXtreamCatchupUrl } from "../lib/xtreamApi";
import { ArrowLeft, Tv, CalendarRange, Loader2 } from "lucide-react";
import PosterImage from "../components/PosterImage";
import { PLAYER } from "../lib/testIds";

// Each timeline column = 30 min. Pixel width per minute drives the layout.
const PX_PER_MIN = 4;
const SLOT_MIN = 30;
const ROW_HEIGHT = 64;
const CHANNEL_COL_PX = 200;

function fmtTimeLabel(d) {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function fmtDayLabel(d) {
  return d.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
}

export default function TvGuide() {
  const navigate = useNavigate();
  const { playlists } = useOutletContext();
  const { channels, epgByChannel, status, xtream } = usePlaylistData();

  // Anchor the timeline 2 hours behind now so the user can see catchup +
  // current + next slots without scrolling.
  const [origin] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() - 2);
    return d;
  });
  const TOTAL_HOURS = 14; // 14 hours of timeline = 2 past + 12 future
  const totalSlots = (TOTAL_HOURS * 60) / SLOT_MIN;
  const totalWidthPx = totalSlots * SLOT_MIN * PX_PER_MIN;

  // Only live TV channels
  const liveChannels = useMemo(
    () =>
      (channels || []).filter((c) => c.content_type === "live_tv").slice(0, 500),
    [channels]
  );

  const [filter, setFilter] = useState("");
  const filteredChannels = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return liveChannels;
    return liveChannels.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) || c.group?.toLowerCase().includes(q)
    );
  }, [liveChannels, filter]);

  // Now line: minutes since origin
  const [nowMin, setNowMin] = useState(
    () => (Date.now() - origin.getTime()) / 60000
  );
  useEffect(() => {
    const id = setInterval(
      () => setNowMin((Date.now() - origin.getTime()) / 60000),
      30 * 1000
    );
    return () => clearInterval(id);
  }, [origin]);

  // Scroll the grid so "now" is roughly centred on initial render.
  const gridRef = useRef(null);
  useEffect(() => {
    if (!gridRef.current) return;
    const nowPx = nowMin * PX_PER_MIN;
    gridRef.current.scrollLeft = Math.max(0, nowPx - 240);
  }, [nowMin]);

  const playChannel = (ch) => {
    navigate("/player/watch", {
      state: {
        stream_url: ch.stream_url,
        name: ch.name,
        logo: ch.logo_url || "",
        type: "live_tv",
      },
    });
  };

  const playProgramme = (ch, prog) => {
    const nowTs = Math.floor(Date.now() / 1000);
    // Future programmes can't be streamed yet — just play live and ignore.
    if (prog.start_ts > nowTs) {
      playChannel(ch);
      return;
    }
    // Current programme → live stream.
    if (prog.stop_ts > nowTs) {
      playChannel(ch);
      return;
    }
    // Past programme → Xtream catchup if available.
    if (xtream?.creds && ch.xc_id && ch.tv_archive) {
      const duration = Math.max(1, prog.stop_ts - prog.start_ts);
      const built = buildXtreamCatchupUrl(
        xtream.creds,
        ch.xc_id,
        prog.start_ts,
        duration
      );
      if (built?.primary) {
        navigate("/player/watch", {
          state: {
            stream_url: built.primary,
            fallback_url: built.fallback,
            name: `${ch.name} — ${prog.title || "Catch-up"}`,
            logo: ch.logo_url || "",
            type: "catchup",
          },
        });
        return;
      }
    }
    // No catchup → fall back to live.
    playChannel(ch);
  };

  // Build the timeline header labels (every 30 min)
  const headerSlots = useMemo(() => {
    const slots = [];
    for (let i = 0; i < totalSlots; i++) {
      const t = new Date(origin.getTime() + i * SLOT_MIN * 60000);
      slots.push({ t, key: i });
    }
    return slots;
  }, [origin, totalSlots]);

  if (playlists.length === 0) {
    return (
      <div className="py-24 text-center text-slate-400">
        <Tv className="w-10 h-10 mx-auto mb-3 opacity-50" />
        <p>No playlists assigned to this device.</p>
      </div>
    );
  }

  const nowPx = nowMin * PX_PER_MIN;
  const originDayLabel = fmtDayLabel(origin);

  return (
    <div data-testid={PLAYER.tvGuideScreen} className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          data-testid={PLAYER.btnBack}
          onClick={() => navigate("/player/menu")}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Menu
        </button>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <CalendarRange className="w-5 h-5 text-orange-400" />
          TV Guide
        </h2>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500 hidden sm:inline">
            {originDayLabel}
          </span>
          <input
            data-testid="tvguide-channel-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter channels…"
            className="bg-[#11141b] border border-white/10 focus:border-orange-500/50 text-sm rounded-md px-3 py-1.5 outline-none placeholder:text-slate-600 w-56"
          />
        </div>
      </div>

      {status === "loading" ? (
        <div className="py-24 flex flex-col items-center text-slate-400">
          <Loader2 className="w-8 h-8 animate-spin mb-3" />
          <span className="text-sm">Loading guide…</span>
        </div>
      ) : filteredChannels.length === 0 ? (
        <div className="py-24 text-center text-slate-500 text-sm">
          No live channels in this playlist.
        </div>
      ) : (
        <div className="border border-white/5 rounded-xl overflow-hidden bg-[#0d1016]">
          {/* Scrollable timeline area */}
          <div ref={gridRef} className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
            <div
              className="relative"
              style={{ width: CHANNEL_COL_PX + totalWidthPx }}
            >
              {/* Header row (sticky top) */}
              <div
                className="sticky top-0 z-20 flex bg-[#0d1016]/95 backdrop-blur border-b border-white/10"
                style={{ height: 36 }}
              >
                <div
                  className="shrink-0 border-r border-white/10 flex items-center px-3 text-[11px] uppercase tracking-wider text-slate-500"
                  style={{ width: CHANNEL_COL_PX }}
                >
                  Channel
                </div>
                <div className="relative" style={{ width: totalWidthPx }}>
                  {headerSlots.map((s) => (
                    <div
                      key={s.key}
                      className="absolute top-0 bottom-0 flex items-center text-[11px] text-slate-400 border-l border-white/5 px-2"
                      style={{
                        left: s.key * SLOT_MIN * PX_PER_MIN,
                        width: SLOT_MIN * PX_PER_MIN,
                      }}
                    >
                      {fmtTimeLabel(s.t)}
                    </div>
                  ))}
                </div>
              </div>

              {/* Now line spans full height */}
              {nowPx >= 0 && nowPx <= totalWidthPx && (
                <div
                  className="absolute top-0 bottom-0 w-[2px] bg-orange-500 z-10 pointer-events-none"
                  style={{ left: CHANNEL_COL_PX + nowPx }}
                >
                  <div className="absolute -top-1 -left-1 w-2.5 h-2.5 rounded-full bg-orange-500 ring-2 ring-[#0d1016]" />
                </div>
              )}

              {/* Channel rows */}
              {filteredChannels.map((ch) => {
                const epg = ch.tvg_id ? epgByChannel[ch.tvg_id] || [] : [];
                const originTs = Math.floor(origin.getTime() / 1000);
                const endTs = originTs + TOTAL_HOURS * 3600;
                const visiblePrograms = epg.filter(
                  (p) => p.stop_ts > originTs && p.start_ts < endTs
                );

                return (
                  <div
                    key={ch.id}
                    data-testid={`tvguide-row-${ch.id}`}
                    className="flex border-b border-white/5 last:border-b-0"
                    style={{ height: ROW_HEIGHT }}
                  >
                    {/* Sticky channel cell */}
                    <button
                      onClick={() => playChannel(ch)}
                      className="sticky left-0 z-10 shrink-0 bg-[#0d1016] hover:bg-[#161a23] border-r border-white/10 flex items-center gap-2 px-3 text-left"
                      style={{ width: CHANNEL_COL_PX }}
                      title={`Play ${ch.name} live`}
                    >
                      <div className="w-9 h-9 shrink-0 bg-black rounded overflow-hidden flex items-center justify-center">
                        {ch.logo_url ? (
                          <PosterImage
                            src={ch.logo_url}
                            alt=""
                            fallbackIcon={Tv}
                            fallbackIconClassName="w-4 h-4 text-slate-700"
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Tv className="w-4 h-4 text-slate-700" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{ch.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {ch.group || "—"}
                        </div>
                      </div>
                    </button>

                    {/* Programme blocks */}
                    <div className="relative" style={{ width: totalWidthPx }}>
                      {visiblePrograms.length === 0 ? (
                        <div className="absolute inset-0 flex items-center justify-center text-[11px] text-slate-700 italic">
                          No EPG
                        </div>
                      ) : (
                        visiblePrograms.map((p) => {
                          const startMin = (p.start_ts - originTs) / 60;
                          const stopMin = (p.stop_ts - originTs) / 60;
                          const left = Math.max(0, startMin * PX_PER_MIN);
                          const width =
                            Math.min(stopMin, totalSlots * SLOT_MIN) * PX_PER_MIN - left;
                          if (width < 8) return null;
                          const nowTs = Date.now() / 1000;
                          const isLive = p.start_ts <= nowTs && p.stop_ts > nowTs;
                          const isPast = p.stop_ts <= nowTs;
                          const canCatchup =
                            isPast && xtream?.creds && ch.xc_id && ch.tv_archive;
                          const cls = isLive
                            ? "bg-orange-500/20 hover:bg-orange-500/35 border-orange-500/60 text-orange-100"
                            : canCatchup
                            ? "bg-emerald-500/10 hover:bg-emerald-500/25 border-emerald-500/40 text-emerald-100"
                            : isPast
                            ? "bg-white/[0.02] border-white/5 text-slate-600 cursor-default"
                            : "bg-white/[0.04] hover:bg-white/[0.08] border-white/10 text-slate-200";
                          const disabled = isPast && !canCatchup;
                          return (
                            <button
                              key={`${p.start_ts}-${p.title}`}
                              data-testid={`tvguide-prog-${ch.id}-${p.start_ts}`}
                              disabled={disabled}
                              onClick={() => !disabled && playProgramme(ch, p)}
                              className={`absolute top-1 bottom-1 rounded-md px-2 py-1 text-left border overflow-hidden transition-colors ${cls}`}
                              style={{ left, width: width - 2 }}
                              title={`${p.title || "Untitled"}\n${fmtTimeLabel(
                                new Date(p.start_ts * 1000)
                              )} – ${fmtTimeLabel(new Date(p.stop_ts * 1000))}`}
                            >
                              <div className="text-[12px] font-medium truncate leading-tight">
                                {p.title || "Untitled"}
                              </div>
                              <div className="text-[10px] opacity-70 truncate">
                                {fmtTimeLabel(new Date(p.start_ts * 1000))}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-2 border-t border-white/10 text-[11px] text-slate-500 flex items-center gap-4 flex-wrap bg-[#0a0c11]">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-orange-500/60 inline-block" />
              Now playing
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/50 inline-block" />
              Catch-up available
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-white/10 inline-block" />
              Upcoming
            </span>
            <span className="ml-auto hidden md:inline">
              Tip: tap a channel name to play it live.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
